import { useCallback, useState } from "react";
import { Box, Card, CardBody, Divider, Flex, HStack, VStack } from "@chakra-ui/react";

import AppPage from "layouts/AppPage";
import { useParams, useSearchParams } from "react-router";
import { checkpointKeys, useCheckpointDetailQuery } from "features/Resources/Checkpoint/hooks";
import QueryError from "components/Error/QueryError";
import ActionPageNewHireHeader from "features/Resources/Shared/Components/ActionPageNewHireHeader";
import { usePlatformEventCable, CollapsibleHistory } from "features/PlatformEvents";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyPathById } from "helpers/url";
import { truncateEnd } from "helpers/string";
import CDS, { ActionTypeTag } from "design-system";
import { ShowName } from "features/Resources/Shared/Components/Fields/NameField";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";
import CheckpointStatusBar from "features/Resources/Checkpoint/Components/CheckpointStatusBar";
import CheckpointTasksSection, {
  type CheckpointTaskStats,
} from "features/Resources/Checkpoint/Components/CheckpointTasksSection";
import CheckpointMetaCard from "features/Resources/Checkpoint/Components/CheckpointMetaCard";
import CheckpointCommunicationsSection from "features/Resources/Checkpoint/Components/CheckpointCommunicationsSection";
import CheckpointSendRemindersButton from "features/Resources/Checkpoint/Components/CheckpointSendRemindersButton";

export default function CheckpointPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data, isLoading, isError, error: queryError } = useCheckpointDetailQuery(id);
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(data?.newHireJourneyId);
  const defaultOpenReminders = searchParams.get("reminders") === "open";
  const [liveStats, setLiveStats] = useState<CheckpointTaskStats | null>(null);
  const handleStatsChange = useCallback((stats: CheckpointTaskStats) => setLiveStats(stats), []);

  usePlatformEventCable({
    entityType: "Checkpoint",
    entityId: data?.id,
    enabled: !!data?.id,
    detailQueryKeys: data?.id ? checkpointKeys.detail(data.id) : undefined,
  });

  if (!id) {
    return <AppPage>Checkpoint not found</AppPage>;
  }

  if (isLoading) {
    return <AppPage>Loading...</AppPage>;
  }

  if (!data && isError) {
    return <QueryError queryError={queryError} resourceName="Checkpoint" id={id} />;
  }

  const isEvaluated = data?.evaluatedAt != null;

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "New Hire Journeys", toUrl: INTERNAL_TEAM_ROUTES.newHireJourneys.index },
          {
            name: isFetched && newHireJourney?.newHire.fullName ? `${newHireJourney.newHire.fullName}` : "Onboarding",
            toUrl: newHireJourneyPathById(newHireJourney?.id),
          },
          { name: `Checkpoint '${truncateEnd(data?.name)}'` },
        ]}
      />
      {data && (
        <VStack spacing="4" align="stretch">
          {/* Header card */}
          <Card overflow="hidden">
            <ActionPageNewHireHeader newHireJourneyId={data?.newHireJourneyId} />
            <CardBody>
              <HStack justify="space-between" mb="2">
                <HStack align="center" height="40px">
                  <ActionTypeTag.CheckpointIconBadge size="md" />
                  <ShowName name={data.name} />
                </HStack>
                <HStack spacing="2">
                  <CheckpointSendRemindersButton
                    status={data.status}
                    newHireJourney={newHireJourney}
                    results={data.results}
                    defaultOpen={defaultOpenReminders}
                  />
                  {!["removed"].includes(data.status || "") && (
                    <ResourceOptionMenu prefixId={data?.prefixId} includeTemplate />
                  )}
                </HStack>
              </HStack>
              <CheckpointStatusBar data={data} />
            </CardBody>
          </Card>

          <CheckpointMetaCard data={data} />

          {/* Stats row */}
          {isEvaluated && liveStats && (
            <HStack
              spacing={0}
              align="stretch"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              bg="white"
              divider={<Divider orientation="vertical" borderColor="gray.200" h="auto" alignSelf="stretch" />}
            >
              <Box px="5" py="3">
                <CDS.Text10
                  fontWeight="600"
                  color="content.tertiary"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Tasks Completed
                </CDS.Text10>
                <CDS.Text18
                  fontSize="xl"
                  fontWeight="600"
                  color={liveStats.tasksComplete === liveStats.tasksTotal ? "green.600" : "content.primary"}
                >
                  {liveStats.tasksComplete} / {liveStats.tasksTotal}
                </CDS.Text18>
              </Box>
              <Box px="5" py="3">
                <CDS.Text10
                  fontWeight="600"
                  color="content.tertiary"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Overdue
                </CDS.Text10>
                <CDS.Text18 fontSize="xl" fontWeight="600" color={liveStats.tasksOverdue > 0 ? "red.600" : "content.primary"}>
                  {liveStats.tasksOverdue}
                </CDS.Text18>
              </Box>
              <Box px="5" py="3">
                <CDS.Text10
                  fontWeight="600"
                  color="content.tertiary"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Assignees with Open Tasks
                </CDS.Text10>
                <CDS.Text18
                  fontSize="xl"
                  fontWeight="600"
                  color={liveStats.followUpCount > 0 ? "orange.500" : "content.primary"}
                >
                  {liveStats.followUpCount}
                </CDS.Text18>
              </Box>
            </HStack>
          )}

          {/* Not evaluated placeholder */}
          {!isEvaluated && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="10"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              bg="white"
            >
              <CDS.Text18 fontSize="2xl" mb="2">
                📋
              </CDS.Text18>
              <CDS.Text12 fontWeight="500" color="content.primary">
                Not run yet
              </CDS.Text12>
              <CDS.Text10 color="content.tertiary" mt="0.5">
                Stats and task status will appear after the checkpoint runs.
              </CDS.Text10>
            </Flex>
          )}

          {/* Tasks card */}
          {isEvaluated && (
            <Card>
              <CardBody>
                <CheckpointTasksSection data={data} onStatsChange={handleStatsChange} />
              </CardBody>
            </Card>
          )}

          {/* Communications card */}
          {isEvaluated && (
            <Card>
              <CardBody>
                <CheckpointCommunicationsSection data={data} checkpointId={String(data.id)} />
              </CardBody>
            </Card>
          )}

          {/* History card */}
          <Card>
            <CardBody>
              <CollapsibleHistory
                entityId={data.id}
                entityType="Checkpoint"
                eventType="checkpoint"
                showNewHireReference={false}
                withDivider={false}
              />
            </CardBody>
          </Card>
        </VStack>
      )}
    </AppPage>
  );
}
